import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { HighlightRole } from '../core/Enums';
import type { HighlightTarget, SpotlightConfig } from '../core/Types';
import { useTour } from '../core/TourProvider';
import styles from '../stylesheets/tourOverlay.module.scss';

const FADE_DURATION = 300;

type StyledEntry = {
  el: HTMLElement;
  classes: string[];
  background?: string;
  radius: string;
};

const resolveHighlights = (
  spotlight: SpotlightConfig | undefined
): HighlightTarget[] => {
  if (!spotlight) return [];
  if (spotlight.highlights) {
    return spotlight.highlights;
  }
  return [{ anchor: spotlight.anchor, role: HighlightRole.ACTIONABLE }];
};

const hasRoundedCorner = (radius: string) => {
  const values = radius.match(/-?\d*\.?\d+/g);
  if (!values) {
    return false;
  }
  return values.some((value) => Number(value) > 0);
};

const resolveTargetRadius = (el: HTMLElement) => {
  const ownRadius = window.getComputedStyle(el).borderRadius;
  if (hasRoundedCorner(ownRadius)) {
    return ownRadius;
  }

  const firstChild = el.firstElementChild;
  if (firstChild instanceof HTMLElement) {
    const childRadius = window.getComputedStyle(firstChild).borderRadius;
    if (hasRoundedCorner(childRadius)) {
      return childRadius;
    }
  }

  return ownRadius;
};

const isTransparentColor = (value: string) => {
  if (!value) return true;
  if (value === 'transparent') return true;

  const match = value.match(/^rgba\(([^)]+)\)$/i);
  if (!match) return false;

  const parts = match[1]?.split(',').map((part) => part.trim());
  if (!parts || parts.length < 4) return false;

  const alpha = Number(parts[3]);
  return Number.isFinite(alpha) && alpha === 0;
};

const findScrollableAt = (
  overlay: HTMLElement,
  x: number,
  y: number
): HTMLElement | null => {
  overlay.style.pointerEvents = 'none';
  const target = document.elementFromPoint(x, y);
  overlay.style.pointerEvents = '';

  let node = target as HTMLElement | null;
  while (node && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const TourOverlay = () => {
  const { currentStep, currentSubscene, getAnchorEl, anchorTick } = useTour();
  const spotlight = currentSubscene?.spotlight ?? currentStep?.spotlight;
  const styledElementsRef = useRef<StyledEntry[]>([]);
  const fadeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    fadeTimersRef.current.forEach(clearTimeout);
    fadeTimersRef.current = [];

    const prev = styledElementsRef.current;
    const highlights = resolveHighlights(spotlight);

    const mergedEntries = new Map<HTMLElement, StyledEntry>();
    highlights.forEach((target) => {
      const el = getAnchorEl(target.anchor) as HTMLElement | null;
      if (!el || !el.isConnected) return;
      const existingEntry = mergedEntries.get(el);
      if (existingEntry) {
        if (target.role === HighlightRole.HIGHLIGHT) {
          existingEntry.background = 'white';
        } else if (
          target.role === HighlightRole.ACTIONABLE &&
          !existingEntry.background &&
          isTransparentColor(window.getComputedStyle(el).backgroundColor)
        ) {
          existingEntry.background = 'white';
        }
        if (
          target.role === HighlightRole.ACTIONABLE &&
          !existingEntry.classes.includes(styles.actionableTarget)
        ) {
          existingEntry.classes.push(styles.actionableTarget);
        }
        return;
      }

      const classes: string[] = [styles.highlightTarget];
      if (spotlight?.style === 'compact') {
        classes.push(styles.compactSpotlightTarget);
      }
      if (target.role === HighlightRole.ACTIONABLE) {
        classes.push(styles.actionableTarget);
      }

      const radius = resolveTargetRadius(el);
      mergedEntries.set(el, {
        el,
        classes,
        background:
          target.role === HighlightRole.HIGHLIGHT ||
          (target.role === HighlightRole.ACTIONABLE &&
            isTransparentColor(window.getComputedStyle(el).backgroundColor))
            ? 'white'
            : undefined,
        radius,
      });
    });
    const newEntries = Array.from(mergedEntries.values());

    const newEls = new Set(newEntries.map((e) => e.el));
    const prevByEl = new Map(prev.map((entry) => [entry.el, entry]));

    prev.forEach(({ el, classes }) => {
      if (!newEls.has(el)) {
        classes.forEach((cls) => el.classList.remove(cls));
        el.style.removeProperty('background');
        el.style.removeProperty('--pt-highlight-radius');
        el.classList.add(styles.highlightFadeOut);
        const timer = setTimeout(() => {
          el.classList.remove(styles.highlightFadeOut);
        }, FADE_DURATION);
        fadeTimersRef.current.push(timer);
      }
    });

    newEntries.forEach(({ el, classes, background, radius }) => {
      const prevEntry = prevByEl.get(el);

      if (!prevEntry) {
        classes.forEach((cls) => el.classList.add(cls));
      } else {
        const nextClasses = new Set(classes);
        const prevClasses = new Set(prevEntry.classes);
        prevEntry.classes.forEach((cls) => {
          if (!nextClasses.has(cls)) {
            el.classList.remove(cls);
          }
        });
        classes.forEach((cls) => {
          if (!prevClasses.has(cls)) {
            el.classList.add(cls);
          }
        });
      }

      el.style.setProperty('--pt-highlight-radius', radius);
      if (background) {
        el.style.background = background;
      } else {
        el.style.removeProperty('background');
      }
    });

    styledElementsRef.current = newEntries;

    return () => {
      fadeTimersRef.current.forEach(clearTimeout);
      fadeTimersRef.current = [];
      styledElementsRef.current.forEach(({ el, classes }) => {
        classes.forEach((cls) => el.classList.remove(cls));
        el.style.removeProperty('background');
        el.style.removeProperty('--pt-highlight-radius');
      });
      styledElementsRef.current = [];
    };
  }, [spotlight, getAnchorEl, anchorTick]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const handleWheel = (e: WheelEvent) => {
      const scrollable = findScrollableAt(overlay, e.clientX, e.clientY);
      if (scrollable) {
        scrollable.scrollTop += e.deltaY;
        e.preventDefault();
      }
    };

    let touchStartY = 0;
    let scrollTarget: HTMLElement | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      touchStartY = t.clientY;
      scrollTarget = findScrollableAt(overlay, t.clientX, t.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!scrollTarget) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = touchStartY - t.clientY;
      scrollTarget.scrollTop += dy;
      touchStartY = t.clientY;
      e.preventDefault();
    };

    const handleTouchEnd = () => {
      scrollTarget = null;
    };

    overlay.addEventListener('wheel', handleWheel, { passive: false });
    overlay.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    overlay.addEventListener('touchmove', handleTouchMove, { passive: false });
    overlay.addEventListener('touchend', handleTouchEnd);

    return () => {
      overlay.removeEventListener('wheel', handleWheel);
      overlay.removeEventListener('touchstart', handleTouchStart);
      overlay.removeEventListener('touchmove', handleTouchMove);
      overlay.removeEventListener('touchend', handleTouchEnd);
    };
  }, [!!spotlight]);

  if (!spotlight) return null;

  return <div ref={overlayRef} className={styles.overlay} />;
};

export default TourOverlay;
